import React from "react";
import axios from "axios";
import { X, Copy, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "novalynx_exit_intent_v1";
const IDLE_MS_MOBILE = 30_000; // fallback trigger on mobile (no mouseleave)

/**
 * Exit-intent coupon modal.
 * - Shows once per browser (localStorage).
 * - Desktop: fires when the cursor moves toward the top of the viewport (mouseleave to top).
 * - Mobile: fires after 30s of activity (mouseleave rarely fires on touch).
 * - Skipped entirely for signed-in users (they already converted).
 */
export default function ExitIntentModal() {
  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [revealed, setRevealed] = React.useState(null); // { code, percent_off }
  const [copied, setCopied] = React.useState(false);
  const firedRef = React.useRef(false);

  const shouldSkip = React.useCallback(() => {
    try {
      if (localStorage.getItem(KEY) === "shown") return true;
      if (localStorage.getItem("vm_spin_seen")) return true; // SpinWheel already served the same purpose
      const auth = localStorage.getItem("novalynx_auth");
      if (auth && JSON.parse(auth)?.state?.user) return true; // already a customer
    } catch { /* ignore */ }
    return false;
  }, []);

  const trigger = React.useCallback(() => {
    if (firedRef.current || shouldSkip()) return;
    firedRef.current = true;
    setOpen(true);
    try { localStorage.setItem(KEY, "shown"); } catch { /* ignore */ }
  }, [shouldSkip]);

  React.useEffect(() => {
    if (shouldSkip()) return;
    const isTouch = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    const onMouseOut = (e) => {
      // fires when cursor exits the document toward the top (URL bar / tab)
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };
    let timer;
    if (isTouch) {
      timer = setTimeout(trigger, IDLE_MS_MOBILE);
    } else {
      document.addEventListener("mouseout", onMouseOut);
    }
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      if (timer) clearTimeout(timer);
    };
  }, [trigger, shouldSkip]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/marketing/subscribe`, { email: email.trim(), source: "exit_intent" });
      setRevealed(r.data);
      // Persist so Checkout can auto-apply the code
      try { localStorage.setItem("novalynx_pending_coupon", r.data.code); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
    setBusy(false);
  };

  const copyCode = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.code);
      setCopied(true);
      toast.success("Code copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.info("Code: " + revealed.code);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      data-testid="exit-intent-modal"
      onClick={() => setOpen(false)}
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-card border border-primary/30 shadow-[0_20px_60px_-20px_rgba(244,192,58,0.35)] p-8 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 h-8 w-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-muted-foreground hover:text-white transition"
          aria-label="Close"
          data-testid="exit-intent-close"
        >
          <X className="h-4 w-4" />
        </button>

        {revealed ? (
          <div className="text-center" data-testid="exit-intent-revealed">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-bold uppercase tracking-widest mb-4">
              <Sparkles className="h-3.5 w-3.5" /> Code unlocked
            </div>
            <h2 className="font-display text-3xl font-bold mb-2">You're in.</h2>
            <p className="text-sm text-muted-foreground mb-6">We sent it to your inbox too. Paste it at checkout to save {Math.round(revealed.percent_off)}%.</p>
            <div className="rounded-2xl border-2 border-dashed border-primary/50 bg-primary/5 p-6 mb-5">
              <p className="text-[10px] uppercase tracking-widest text-primary font-bold mb-1">Your code</p>
              <div className="flex items-center justify-center gap-3">
                <span className="font-mono text-3xl font-black tracking-widest" data-testid="exit-intent-code">{revealed.code}</span>
                <button
                  onClick={copyCode}
                  className="h-9 w-9 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center transition"
                  aria-label="Copy code"
                  data-testid="exit-intent-copy"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <a href="/shop" className="inline-block w-full">
              <Button className="btn-primary w-full h-11" data-testid="exit-intent-shop">Shop with my discount</Button>
            </a>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="exit-intent-form">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-bold uppercase tracking-widest mb-4">
              <Sparkles className="h-3.5 w-3.5" /> Wait — one sec
            </div>
            <h2 className="font-display text-3xl font-bold leading-tight mb-2">
              Get <span className="text-primary">10% off</span> your first order
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Drop your email and we'll send a code you can use right now — plus early looks at product drops.
            </p>
            <div className="space-y-3">
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                data-testid="exit-intent-email"
                className="h-12 text-base"
              />
              <Button type="submit" className="btn-primary w-full h-12 text-base" disabled={busy} data-testid="exit-intent-submit">
                {busy ? "Sending…" : "Get my 10% off"}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground text-center mt-4">
              No spam. Unsubscribe any time.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
